import { useState } from "react";
import { ArrowRight, CalendarDays, LockKeyhole, Menu, ShieldCheck, UsersRound, X } from "lucide-react";

type Pillar = {
  kicker: string;
  title: string;
  body: string;
};

type Session = {
  number: string;
  label: string;
  title: string;
  sub: string;
  body: string;
  themes: string[];
};

const pillars: Pillar[] = [
  {
    kicker: "01",
    title: "Player Defense Fund",
    body: "A pooled legal, communications, and rapid-response resource for players facing institutional, brand, or media pressure after speaking publicly.",
  },
  {
    kicker: "02",
    title: "Sovereign Media",
    body: "A player-controlled media entity for distribution, editorial independence, live formats, and direct audience ownership.",
  },
  {
    kicker: "03",
    title: "Creator Fund",
    body: "A dedicated fund to greenlight player-led content, documentaries, series, studios, and creator ventures without waiting on legacy gatekeepers.",
  },
];

const sessions: Session[] = [
  {
    number: "01",
    label: "Media & Narrative",
    title: "Who Controls the Story?",
    sub: "The gap between what players do and what gets reported.",
    body: "Legacy media rewards conflict, compression, and speed. This session maps where narrative control breaks down and what players can own directly.",
    themes: [
      "Editorial incentives and pressure loops",
      "Direct-to-community distribution",
      "Accountability for misinformation",
      "Building the record when media will not",
    ],
  },
  {
    number: "02",
    label: "Rights & Expression",
    title: "The Cost of Speaking Freely",
    sub: "What it takes to protect players who refuse to stay on script.",
    body: "Players who speak on human rights, conflict, and systemic injustice often face the same sequence: brand risk, media escalation, institutional pressure, and isolation.",
    themes: [
      "Athlete activism and institutional response",
      "Coalition as protection",
      "Global rights frameworks in sports",
      "Speech rights versus institutional safety",
    ],
  },
  {
    number: "03",
    label: "Ownership Economy",
    title: "From Influence to Equity",
    sub: "Attention is not ownership. Followers are not leverage.",
    body: "Players create economic value for leagues, networks, brands, and platforms. The working question is how that cultural authority converts into durable ownership.",
    themes: [
      "Collective NIL infrastructure",
      "Creator-owned platforms",
      "Aligned family-office and sovereign capital",
      "IP, studios, and content rights",
    ],
  },
  {
    number: "04",
    label: "Technology & Identity",
    title: "Who Owns You in the Digital Age?",
    sub: "AI, likeness, deepfakes, and the next frontier of extraction.",
    body: "Player voice, image, and identity are increasingly used to train systems and create synthetic media without consent, compensation, or practical recourse.",
    themes: [
      "AI likeness consent gaps",
      "Deepfakes and reputational harm",
      "Collective NIL as defense",
      "Policy players should push",
    ],
  },
  {
    number: "05",
    label: "Coalition & Continuity",
    title: "Building the Room That Stays",
    sub: "The summit is one day. The community is permanent.",
    body: "The closing session turns conversation into operating commitments: who stays connected, what gets built, and how the next player is supported.",
    themes: [
      "Cross-team solidarity",
      "Veteran-to-rookie continuity",
      "Protective infrastructure",
      "Decisions over declarations",
    ],
  },
];

const principles = [
  {
    title: "Safety Through Numbers",
    body: "When multiple players across teams and generations share a position, no institution can easily isolate one voice.",
  },
  {
    title: "Confidentiality by Design",
    body: "No public guest list. No attributed transcripts. No recordings without consent. The work belongs to the people in the room.",
  },
  {
    title: "Bring Your Circle",
    body: "Players are encouraged to bring trusted teammates, advisors, and community members who help carry the work after the summit.",
  },
  {
    title: "No Extraction",
    body: "This is not a content farm, press event, or sponsor platform. There are no media partners with editorial rights.",
  },
];

function SummitBadge({ children }: { children: React.ReactNode }) {
  return <span className="summit-badge">{children}</span>;
}

function CtaLink({
  href,
  children,
  variant = "premium",
}: {
  href: string;
  children: React.ReactNode;
  variant?: "premium" | "outline";
}) {
  return (
    <a className={`button-cta button-cta-${variant}`} href={href}>
      {children}
    </a>
  );
}

function Nav() {
  const [isOpen, setIsOpen] = useState(false);

  const closeMenu = () => setIsOpen(false);

  return (
    <header className="site-nav">
      <a className="brand-mark" href="#top" aria-label="The 2026 Players Summit home" onClick={closeMenu}>
        2026 Players Summit
      </a>
      <button
        className="menu-toggle"
        type="button"
        aria-expanded={isOpen}
        aria-label={isOpen ? "Close navigation" : "Open navigation"}
        aria-controls="primary-navigation"
        onClick={() => setIsOpen((current) => !current)}
      >
        {isOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
      </button>
      <nav className={isOpen ? "is-open" : ""} id="primary-navigation" aria-label="Primary navigation">
        <a href="#about" onClick={closeMenu}>About</a>
        <a href="#program" onClick={closeMenu}>Program</a>
        <a href="#community" onClick={closeMenu}>Community</a>
        <a href="#register" onClick={closeMenu}>Invitation</a>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="hero-shade" />
      <div className="hero-content">
        <SummitBadge>NBPA · 2026 · Strictly Private</SummitBadge>
        <h1>
          Own the Game.
          <span>Own the Voice.</span>
        </h1>
        <p>
          A private, player-led summit for candid conversation, collective protection, and the infrastructure players need beyond the mic.
        </p>
        <div className="hero-actions">
          <CtaLink href="#register">
            Request an Invitation <ArrowRight aria-hidden="true" />
          </CtaLink>
          <CtaLink href="#program" variant="outline">
            View Sessions
          </CtaLink>
        </div>
      </div>
      <div className="hero-panel" aria-label="Summit details">
        <div>
          <CalendarDays aria-hidden="true" />
          <span>Summer 2026</span>
        </div>
        <div>
          <LockKeyhole aria-hidden="true" />
          <span>Invite Only</span>
        </div>
        <div>
          <UsersRound aria-hidden="true" />
          <span>Player-Led</span>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="section about" id="about">
      <div className="section-copy">
        <p className="eyebrow">About the Summit</p>
        <h2>A room built for honest conversation.</h2>
        <p>
          The 2026 Players Summit is a closed working session, not a press event or stage show. Players and their trusted communities gather to speak without consequence, set shared priorities, and build the structures that protect free expression inside and outside the game.
        </p>
        <p>
          Outputs are decisions, working groups, and commitments. No outside media. No public guest list. Nothing leaves the room without consent.
        </p>
        <div className="partnership-card">
          <p>Presented in partnership with</p>
          <div className="partner-logos" aria-label="NBPA, PLYRS UNTD, and Factory Club">
            <img src="/images/nbpa-logo.png" alt="NBPA" />
            <span>PLYRS UNTD</span>
            <img src="/images/factory-club-logo.png" alt="Factory Club" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="manifesto" aria-label="Summit thesis">
      <p>
        When players speak freely and face consequences for it, the problem is not the player. It is the absence of an institution designed to protect them.
      </p>
      <span>This summit builds that institution.</span>
    </section>
  );
}

function Pillars() {
  return (
    <section className="section">
      <div className="section-heading">
        <p className="eyebrow">Outcome Pillars</p>
        <h2>What leaves the room.</h2>
      </div>
      <div className="pillar-grid">
        {pillars.map((pillar) => (
          <article className="pillar-card" key={pillar.title}>
            <span>{pillar.kicker}</span>
            <h3>{pillar.title}</h3>
            <p>{pillar.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Program() {
  return (
    <section className="program" id="program">
      <div className="section-heading">
        <p className="eyebrow">Program</p>
        <h2>The working sessions.</h2>
        <p>Closed working groups built around decisions, not transcripts.</p>
      </div>
      <div className="session-list">
        {sessions.map((session) => (
          <article className="session" key={session.number}>
            <div className="session-num">Session {session.number}</div>
            <div className="session-body">
              <p className="session-label">{session.label}</p>
              <h3>{session.title}</h3>
              <strong>{session.sub}</strong>
              <p>{session.body}</p>
              <ul>
                {session.themes.map((theme) => (
                  <li key={theme}>{theme}</li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Community() {
  return (
    <section className="section community" id="community">
      <div className="section-copy">
        <p className="eyebrow">Community Principles</p>
        <h2>This room exists so no one stands alone.</h2>
        <p>
          The structure of the summit is the message: private, practical, player-centered, and built to continue after the event ends.
        </p>
      </div>
      <div className="principle-grid">
        {principles.map((principle) => (
          <article key={principle.title}>
            <ShieldCheck aria-hidden="true" />
            <h3>{principle.title}</h3>
            <p>{principle.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Register() {
  return (
    <section className="closing" id="register">
      <img src="/images/arena.jpg" alt="Arena seating before an event" />
      <div>
        <p className="eyebrow">The 2026 Players Summit</p>
        <h2>
          Protect the culture.
          <span>Own the culture.</span>
        </h2>
        <p>By invitation · Private · Player-led</p>
        <div className="hero-actions">
          <CtaLink href="mailto:events@thefactoryhq.com?subject=2026%20Players%20Summit%20Invitation%20Request">
            Request an Invitation <ArrowRight aria-hidden="true" />
          </CtaLink>
        </div>
      </div>
    </section>
  );
}

export function App() {
  return (
    <>
      <Nav />
      <main>
        <Hero />
        <About />
        <Manifesto />
        <Pillars />
        <Program />
        <Community />
        <Register />
      </main>
      <footer className="footer">
        <span>The 2026 Players Summit · NBPA</span>
        <span>Strictly confidential · Credentialed guests only</span>
      </footer>
    </>
  );
}
